<script lang="ts" setup>
	import { Float } from '@headlessui-float/vue'
	import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
	import {
		EllipsisVerticalIcon,
		PencilSquareIcon,
	} from '@heroicons/vue/20/solid'
	import { RouterLink } from 'vue-router'

	import HasPermission from '@/components/HasPermission.vue'

	import { FiltersForm } from '@/internal/modules/providers/composables/useSearchHotels'
	import useStatusHotel from '@/internal/modules/providers/composables/useStatusHotel'
	import { Hotel } from '@/internal/modules/providers/interfaces/Hotel'

	import ModalUpdateCodeAmadeusCode from './ModalUpdateCodeAmadeusCode.vue'

	const { data: status } = useStatusHotel({
		immediate: true,
	})

	interface Props {
		hotels: Hotel[]
		isDropdownAction: boolean
		isLoadingHotels: boolean
		isRefetchingHotels: boolean
	}

	interface Emits {
		(e: 'actionModal'): void
		(e: 'delete', id: number): Promise<void>
		(e: 'restore', id: number): Promise<void>
		(e: 'toggleDropdown'): void
		(e: 'setHotelIdComments', id: number): void
		(e: 'setHotelIdChangeState', id: string, stateId: number): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()

	const stateIcon = (name: string) => {
		switch (name) {
			case 'Activo':
				return 'check'
			case 'Inactivo':
				return 'remove_done'
			case 'Lista Negra':
				return 'delete'
		}
	}

	const stateClass = (name: string) => {
		switch (name) {
			case 'Activo':
				return 'bg-green-500'
			case 'Inactivo':
				return 'bg-red-500'
			case 'Lista Negra':
				return 'bg-black'
		}
	}
</script>
<template>
	<div class="rounded-xl w-full overflow-hidden">
		<table class="w-full">
			<thead class="bg-gray-50">
				<tr>
					<th
						scope="col"
						class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						Hotel
					</th>
					<th
						scope="col"
						class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						Cadena
					</th>
					<th
						scope="col"
						class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						Marca
					</th>
					<th
						scope="col"
						class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						Grupo
					</th>
					<th
						scope="col"
						class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						Estatus
					</th>
					<th
						scope="col"
						class="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
					>
						Acciones
					</th>
				</tr>
			</thead>

			<tbody class="bg-white divide-y divide-gray-200">
				<!-- Is loading state -->
				<tr
					v-if="isLoadingHotels"
					class="text-center py-12 bg-white rounded-xl w-full"
				>
					<td
						colspan="7"
						class="text-center py-12 bg-white rounded-xl w-[1150px]"
					>
						<span class="material-symbols-outlined text-4xl text-gray-400 mb-2"
							>hotel</span
						>
						<p class="text-gray-500">Cargando hoteles...</p>
					</td>
				</tr>

				<!-- Is isRefetching state -->
				<template v-else-if="isRefetchingHotels">
					<tr
						v-for="entry in 15"
						class="text-center bg-white"
						:key="entry"
					>
						<td class="px-6 py-4 whitespace-normal w-full lg:w-[300px]">
							<div class="animate-pulse flex flex-col gap-2">
								<div class="h-4 w-full bg-gray-200 rounded"></div>
							</div>

							<div class="pt-2">
								<div class="animate-pulse flex items-center gap-2">
									<div class="h-4 w-[100px] bg-gray-200 rounded"></div>
								</div>
							</div>
						</td>

						<td class="px-6 py-4 whitespace-nowrap">
							<div
								class="animate-pulse flex items-center gap-2 w-full lg:w-[150px]"
							>
								<div class="h-4 w-full bg-gray-200 rounded"></div>
							</div>
						</td>

						<td class="px-6 py-4 whitespace-nowrap">
							<div
								class="animate-pulse flex items-center gap-2 w-full lg:w-[150px]"
							>
								<div class="h-4 w-full bg-gray-200 rounded"></div>
							</div>
						</td>

						<td class="px-6 py-4 whitespace-nowrap">
							<div
								class="animate-pulse flex items-center gap-2 w-full lg:w-[150px]"
							>
								<div class="h-4 w-full bg-gray-200 rounded"></div>
							</div>
						</td>

						<td class="px-6 py-4 whitespace-nowrap">
							<div
								class="animate-pulse flex items-center gap-2 w-full lg:w-[100px]"
							>
								<div class="h-4 w-full bg-gray-200 rounded"></div>
							</div>
						</td>

						<td class="px-6 py-4 whitespace-nowrap">
							<div
								class="animate-pulse flex items-center gap-2 w-full lg:w-[100px]"
							>
								<div class="h-4 w-full bg-gray-200 rounded"></div>
							</div>
						</td>
					</tr>
				</template>

				<template v-else-if="hotels.length > 0 && !isLoadingHotels">
					<tr
						v-for="entry in hotels"
						:key="entry.id"
						class="hover:bg-gray-50 transition-colors duration-150"
					>
						<td class="px-6 py-4 whitespace-pre-wrap w-full lg:w-[300px]">
							<div class="text-[15px] capitalize font-bold text-gray-900">
								{{ entry.name }}
							</div>
							<ModalUpdateCodeAmadeusCode :hotel="entry" />
						</td>

						<td
							class="px-6 py-4 whitespace-pre-wrap text-sm text-gray-500 lowercase w-full lg:w-[150px]"
						>
							{{ entry.chainName ?? 'N/A' }}
						</td>
						<td
							class="py-4 whitespace-pre-wrap text-sm text-gray-500 lowercase w-full lg:w-[150px]"
						>
							{{ entry.brandName ?? 'N/A' }}
						</td>
						<td
							class="py-4 whitespace-pre-wrap text-sm text-gray-500 lowercase w-full lg:w-[150px]"
						>
							{{ entry.groupName ?? 'N/A' }}
						</td>
						<td
							class="px-6 py-4 whitespace-nowrap lowercase w-full lg:w-[100px]"
						>
							<span
								:class="`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${stateClass(entry.stateName)} text-white`"
							>
								<span class="material-symbols-outlined text-sm">{{
									stateIcon(entry.stateName)
								}}</span>
								{{ entry.stateName }}
							</span>
						</td>
						<td
							class="px-6 py-4 whitespace-pre-wrap text-center text-sm font-medium lg:w-[100px]"
						>
							<Menu
								as="div"
								class="relative inline-block text-left"
							>
								<Float
									placement="bottom-end"
									:offset="4"
									strategy="fixed"
									flip
								>
									<MenuButton
										class="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
									>
										<EllipsisVerticalIcon
											class="h-5 w-5 text-gray-600"
											aria-hidden="true"
										/>
									</MenuButton>
									<MenuItems
										class="w-56 divide-y divide-gray-100 rounded-xl shadow-xl ring-1 bg-white ring-black ring-opacity-5 focus:outline-none"
									>
										<div class="py-1">
											<HasPermission
												:name-permission="'viewAny-internal'"
												:entity-type="'App\\Models\\Hotel'"
											>
												<MenuItem v-slot="{ active }">
													<RouterLink
														:to="{
															name: 'hotels.hotel.edit',
															params: { id: entry.id },
														}"
														:class="[
															active ?
																'bg-gray-50 text-blue-600'
															:	'text-gray-700',
															'group flex items-center px-4 py-2 text-sm transition-colors duration-150',
														]"
													>
														<PencilSquareIcon
															class="mr-3 h-5 w-5 text-gray-400 group-hover:text-blue-500"
															aria-hidden="true"
														/>
														Editar
													</RouterLink>
												</MenuItem>
											</HasPermission>

											<HasPermission
												:entity-type="'App\\Models\\Hotel'"
												:name-permission="'view-comments-internal'"
											>
												<MenuItem v-slot="{ active, close }">
													<button
														class="w-full"
														:class="[
															active ?
																'bg-gray-100 text-gray-900'
															:	'text-gray-700',
															'group flex items-center px-4 py-2 text-sm gap-2',
														]"
														@click="
															() => {
																emits('setHotelIdComments', entry.id)
																close()
															}
														"
													>
														<small
															class="material-symbols-outlined text-gray-400"
															>history</small
														>
														Historial de comentarios
													</button>
												</MenuItem>
											</HasPermission>

											<HasPermission
												:name-permission="'change-status-internal'"
												:entity-type="'App\\Models\\Hotel'"
											>
												<div
													v-for="state in status"
													:key="state.id"
												>
													<MenuItem
														as="div"
														v-slot="{ close }"
													>
														<button
															v-if="state.id !== entry.stateId"
															class="w-full flex items-center px-4 py-2 text-sm gap-2 hover:bg-gray-100"
															:class="'hover:bg-gray-100 text-gray-700'"
															@click="
																() => {
																	emits(
																		'setHotelIdChangeState',
																		entry.id.toString(),
																		state.id
																	)
																	close()
																}
															"
														>
															<small
																class="material-symbols-outlined text-gray-400"
																>{{ stateIcon(state.displayName) }}</small
															>
															{{ state.displayName }}
														</button>
													</MenuItem>
												</div>
											</HasPermission>
										</div>
									</MenuItems>
								</Float>
							</Menu>
						</td>
					</tr>
				</template>

				<!-- Is has not hotels  -->
				<tr
					v-else
					class="text-center py-12 bg-white rounded-xl shadow-lg"
				>
					<td
						colspan="7"
						class="text-center py-12 bg-white rounded-xl w-[1150px]"
					>
						<span class="material-symbols-outlined text-4xl text-gray-400 mb-2"
							>hotel</span
						>
						<p class="text-gray-500">No hay hoteles disponibles</p>
					</td>
				</tr>
			</tbody>
		</table>
	</div>

	<!-- Pagination -->
	<slot
		name="pagination"
		class="mt-6"
	/>
</template>
